import { useState } from "react";
import { Download, Loader2, RotateCcw, Share2, Check, Save, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { useLocalSimulations } from "@/hooks/useLocalSimulations";
import { trackEvent } from "@/lib/analytics";
import { SavedSimulationsModal } from "../shared/SavedSimulationsModal";
import type { ComputedResults, DiagnosticInsight, SimulationInput } from "../lib/types";
import type { Optimization } from "../lib/diagnostics";

interface Props {
  input: SimulationInput;
  results: ComputedResults;
  insights: DiagnosticInsight[];
  optimizations: Optimization[];
  email: string | null;
  onReset: () => void;
}

export function ActionButtons({ input, results, insights, optimizations, email, onReset }: Props) {
  const [copied, setCopied] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const { save } = useLocalSimulations();

  const handlePDF = async () => {
    if (pdfLoading) return;
    trackEvent("pdf_downloaded");
    setPdfLoading(true);
    try {
      // Lazy: jsPDF + autoTable (~112 KiB) téléchargés uniquement au clic
      const { generatePDF } = await import("../lib/pdf");
      await generatePDF({ input, results, insights, optimizations, email });
    } catch (e) {
      console.error("PDF generation failed", e);
      toast.error("Impossible de générer le PDF. Réessayez.");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSave = () => {
    const name = window.prompt(
      "Nom de la simulation (optionnel) :",
      `Simulation ${input.region} — ${new Intl.NumberFormat("fr-FR").format(input.prixAcquisition)}€`
    );
    if (name === null) return; // Cancel
    save(input, name);
    trackEvent("simulation_saved");
    toast.success("Simulation sauvegardée sur votre appareil");
  };

  const handleShare = async () => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams({
      p: String(input.prixAcquisition),
      t: String(input.travaux),
      m: String(input.mobilier),
      l: String(input.loyerMensuel),
      ty: input.typeLocation,
      v: String(input.tauxVacance),
      cm: input.chargesMode,
      cp: String(input.chargesPct),
      a: String(input.apport),
      hc: input.hasCredit ? "1" : "0",
      me: String(input.montantEmprunte),
      ta: String(input.tauxAnnuel),
      d: String(input.dureeAnnees),
    });
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copiez ce lien :", url);
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-6 py-10 lg:px-12">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <button
          onClick={handlePDF}
          disabled={pdfLoading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-md disabled:cursor-wait disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:border-border disabled:hover:text-foreground"
        >
          {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {pdfLoading ? "Génération…" : "Télécharger en PDF"}
        </button>
        <button
          onClick={handleSave}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-md"
        >
          <Save className="h-4 w-4" />
          Sauvegarder
        </button>
        <button
          onClick={() => setSavedOpen(true)}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-md"
        >
          <FolderOpen className="h-4 w-4" />
          Mes simulations
        </button>
        <button
          onClick={handleShare}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-md"
        >
          {copied ? <Check className="h-4 w-4 text-success" /> : <Share2 className="h-4 w-4" />}
          {copied ? "Lien copié !" : "Partager"}
        </button>
        <button
          onClick={onReset}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-md"
        >
          <RotateCcw className="h-4 w-4" />
          Nouvelle simulation
        </button>
      </div>
      <SavedSimulationsModal open={savedOpen} onOpenChange={setSavedOpen} />
    </section>
  );
}
